import React, { useEffect, useState } from 'react';
import { Modal, ModalDialog, Button } from '@mui/joy';
import { Switch, IconButton, OutlinedInput, FormControl, CircularProgress } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { getFetch, postFetch } from '../../../../fetch/fetch';
import { deadlineToString } from '../../../../etc/regularmealdeadline';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

//마감 기본값 (서버 시드 기본값과 동일: 1일 전 18:00)
const makeDefaultDeadlines = () => ({
    orderLunch: { daysBefore: 1, time: "18:00" },
    orderDinner: { daysBefore: 1, time: "18:00" },
    cancelLunch: { daysBefore: 1, time: "18:00" },
    cancelDinner: { daysBefore: 1, time: "18:00" }
});

const deadlineEditRows = [
    { key: "orderLunch", label: "주문 · 점심" },
    { key: "orderDinner", label: "주문 · 저녁" },
    { key: "cancelLunch", label: "취소 · 점심" },
    { key: "cancelDinner", label: "취소 · 저녁" }
];

//지점별 정기식 설정 (식권 가격 + 정기식 신청 메뉴 노출 여부 + 주문/취소 마감 시간)
const RegularMealSettingModal: React.FC<any> = (props) => {

    const [settings, setSettings] = useState<any>({});
    const [editingLocation, setEditingLocation] = useState<string>("");
    const [editPrice, setEditPrice] = useState<string>("");
    const [editingDeadlineLocation, setEditingDeadlineLocation] = useState<string>("");
    const [editDeadlines, setEditDeadlines] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [savingDeadlines, setSavingDeadlines] = useState<boolean>(false);

    const notifySuccess = (message: string) => toast.success(message, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light"
    });

    const notifyError = (message: string) => toast.error(message, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light"
    });

    const priceToString = (price: number) => {
        return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    //모달 열릴 때 지점별 설정 로드 (행 없는 지점은 기본값 8000원/노출)
    useEffect(() => {

        if (!props.open) {
            return;
        }

        setEditingLocation("");
        setEditPrice("");
        setEditingDeadlineLocation("");
        setEditDeadlines(null);

        const getSettings = async () => {

            try {

                setLoading(true);

                const result: any = await getFetch("/dashboard/restaurant/regularmeal/locationsetting");

                const newSettings: any = {};

                props.locationCandidates.forEach((each: any) => {
                    newSettings[each.english] = { price: 8000, isMenuVisible: true, deadlines: makeDefaultDeadlines() };
                });

                if (result.message === "success" && result.data) {
                    result.data.forEach((each: any) => {
                        if (newSettings[each.location]) {
                            newSettings[each.location] = {
                                price: each.price,
                                isMenuVisible: each.isMenuVisible === 1,
                                //마감 컬럼 없는 구서버 응답 대비 기본값 폴백
                                deadlines: each.orderLunchTime ? {
                                    orderLunch: { daysBefore: +each.orderLunchDaysBefore, time: each.orderLunchTime },
                                    orderDinner: { daysBefore: +each.orderDinnerDaysBefore, time: each.orderDinnerTime },
                                    cancelLunch: { daysBefore: +each.cancelLunchDaysBefore, time: each.cancelLunchTime },
                                    cancelDinner: { daysBefore: +each.cancelDinnerDaysBefore, time: each.cancelDinnerTime }
                                } : makeDefaultDeadlines()
                            };
                        }
                    });
                }

                setSettings(newSettings);

                setLoading(false);

            } catch (e) {
                console.log(e);
                setLoading(false);
            }

        }

        getSettings();

    }, [props.open]);

    //연필 클릭 - 해당 지점 가격 수정 모드로 전환
    const startEdit = (english: string) => {
        setEditingLocation(english);
        setEditPrice(priceToString(settings[english] ? settings[english].price : 8000));
    }

    //가격 저장
    const submitPrice = async (english: string) => {

        try {

            const price = +editPrice.replace(/,/g, "");

            if (!(price > 0)) {
                notifyError("가격을 올바르게 입력해주세요");
                return;
            }

            setSaving(true);

            const result: any = await postFetch("/dashboard/restaurant/regularmeal/locationsetting", {
                location: english,
                price: price
            });

            if (result.message === "success") {
                setSettings((prev: any) => ({ ...prev, [english]: { ...prev[english], price: price } }));
                setEditingLocation("");
                setEditPrice("");
                notifySuccess("가격 저장 성공!");
            } else {
                notifyError("가격 저장 실패!");
            }

            setSaving(false);

        } catch (e) {
            console.log(e);
            setSaving(false);
            notifyError("가격 저장 실패!");
        }

    }

    //노출 토글 - 스위치 변경 즉시 저장
    const toggleVisible = async (english: string, checked: boolean) => {

        try {

            const result: any = await postFetch("/dashboard/restaurant/regularmeal/locationsetting", {
                location: english,
                isMenuVisible: checked
            });

            if (result.message === "success") {
                setSettings((prev: any) => ({ ...prev, [english]: { ...prev[english], isMenuVisible: checked } }));
                notifySuccess(checked ? "정기식 신청 메뉴가 열렸습니다" : "정기식 신청 메뉴가 닫혔습니다");
            } else {
                notifyError("저장 실패!");
            }

        } catch (e) {
            console.log(e);
            notifyError("저장 실패!");
        }

    }

    //마감 연필 클릭 - 해당 지점 마감 수정 모드로 전환
    const startDeadlineEdit = (english: string) => {
        const current = (settings[english] && settings[english].deadlines) ? settings[english].deadlines : makeDefaultDeadlines();
        setEditingDeadlineLocation(english);
        setEditDeadlines(JSON.parse(JSON.stringify(current)));
    }

    //마감 저장 (주문점심/주문저녁/취소점심/취소저녁 4쌍 한번에)
    const submitDeadlines = async (english: string) => {

        try {

            if (!editDeadlines) {
                return;
            }

            const timeRegex = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

            for (const row of deadlineEditRows) {
                if (!timeRegex.test(editDeadlines[row.key].time)) {
                    notifyError("마감 시간을 올바르게 입력해주세요");
                    return;
                }
            }

            setSavingDeadlines(true);

            const result: any = await postFetch("/dashboard/restaurant/regularmeal/locationsetting", {
                location: english,
                deadlines: editDeadlines
            });

            if (result.message === "success") {
                setSettings((prev: any) => ({ ...prev, [english]: { ...prev[english], deadlines: editDeadlines } }));
                setEditingDeadlineLocation("");
                setEditDeadlines(null);
                notifySuccess("마감 시간 저장 성공!");
            } else {
                notifyError("마감 시간 저장 실패!");
            }

            setSavingDeadlines(false);

        } catch (e) {
            console.log(e);
            setSavingDeadlines(false);
            notifyError("마감 시간 저장 실패!");
        }

    }

    //마감 요약 문구 (점심·저녁 같으면 합쳐서 표시)
    const deadlineSummary = (lunch: any, dinner: any) => {
        if (lunch.daysBefore === dinner.daysBefore && lunch.time === dinner.time) {
            return deadlineToString(lunch);
        }
        return `점심 ${deadlineToString(lunch)} · 저녁 ${deadlineToString(dinner)}`;
    }

    return (
        <Modal open={props.open} onClose={props.handleClose}>
            <ModalDialog>
                <div style={{ width: "680px" }}>
                    <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "6px" }}>
                        정기식 지점 설정
                    </div>
                    <div style={{ fontSize: "13px", color: "#888", marginBottom: "16px", lineHeight: 1.5 }}>
                        가격은 식권 1장당 결제선생 청구 금액에 적용됩니다.<br />
                        메뉴 보이기를 끄면 앱에서 정기식 신청 메뉴가 숨겨지고 주문·충전이 차단됩니다.<br />
                        마감 시간은 대상 날짜 기준 N일 전 HH:MM에 주문·취소가 마감됩니다.
                    </div>
                    {
                        loading ?
                            <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                                <CircularProgress />
                            </div>
                            :
                            <div style={{ maxHeight: "480px", overflowY: "auto" }}>
                                <div style={{ display: "flex", alignItems: "center", padding: "6px 4px", borderBottom: "1px solid #e0e0e0", fontSize: "13px", color: "#888" }}>
                                    <div style={{ width: "80px" }}>지점</div>
                                    <div style={{ flex: 1 }}>식권 가격</div>
                                    <div style={{ width: "260px" }}>주문/취소 마감</div>
                                    <div style={{ width: "80px", textAlign: "center" }}>메뉴 보이기</div>
                                </div>
                                {
                                    props.locationCandidates.map((each: any) => {

                                        const setting = settings[each.english] ? settings[each.english] : { price: 8000, isMenuVisible: true, deadlines: makeDefaultDeadlines() };
                                        const isEditing = editingLocation === each.english;
                                        const isDeadlineEditing = editingDeadlineLocation === each.english;
                                        const deadlines = setting.deadlines ? setting.deadlines : makeDefaultDeadlines();

                                        return (
                                            <React.Fragment key={each.english}>
                                            <div style={{ display: "flex", alignItems: "center", padding: "8px 4px", borderBottom: isDeadlineEditing ? "none" : "1px solid #f0f0f0" }}>
                                                <div style={{ width: "80px", fontWeight: 500 }}>
                                                    {each.korean}
                                                </div>
                                                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                                                    {
                                                        isEditing ?
                                                            <>
                                                                <FormControl variant='outlined'>
                                                                    <OutlinedInput
                                                                        size='small'
                                                                        endAdornment={<div>원</div>}
                                                                        sx={{ width: "120px" }}
                                                                        value={editPrice}
                                                                        onChange={(e) => {
                                                                            var value = e.target.value;

                                                                            //value가 2글자 이상인데 0으로 시작하면 0을 제거
                                                                            if (value.length > 1 && value[0] === '0') {
                                                                                value = value.slice(1, value.length);
                                                                            }

                                                                            //0-9만 입력 가능
                                                                            value = value.replace(/[^0-9]/g, '');

                                                                            value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

                                                                            setEditPrice(value);
                                                                        }}
                                                                    />
                                                                </FormControl>
                                                                <Button
                                                                    size="sm"
                                                                    variant="solid"
                                                                    loading={saving}
                                                                    sx={{ marginLeft: "8px" }}
                                                                    onClick={() => submitPrice(each.english)}
                                                                >
                                                                    저장
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="plain"
                                                                    color="neutral"
                                                                    sx={{ marginLeft: "4px" }}
                                                                    onClick={() => { setEditingLocation(""); setEditPrice(""); }}
                                                                >
                                                                    취소
                                                                </Button>
                                                            </>
                                                            :
                                                            <>
                                                                <span>{priceToString(setting.price)}원</span>
                                                                <IconButton size="small" sx={{ marginLeft: "4px" }} onClick={() => startEdit(each.english)}>
                                                                    <CreateIcon sx={{ fontSize: "16px" }} />
                                                                </IconButton>
                                                            </>
                                                    }
                                                </div>
                                                <div style={{ width: "260px", display: "flex", alignItems: "center" }}>
                                                    <div style={{ fontSize: "12px", lineHeight: 1.6 }}>
                                                        <div>주문 {deadlineSummary(deadlines.orderLunch, deadlines.orderDinner)}</div>
                                                        <div>취소 {deadlineSummary(deadlines.cancelLunch, deadlines.cancelDinner)}</div>
                                                    </div>
                                                    <IconButton size="small" sx={{ marginLeft: "4px" }} onClick={() => startDeadlineEdit(each.english)}>
                                                        <CreateIcon sx={{ fontSize: "16px" }} />
                                                    </IconButton>
                                                </div>
                                                <div style={{ width: "80px", textAlign: "center" }}>
                                                    <Switch
                                                        checked={setting.isMenuVisible}
                                                        onChange={(e) => toggleVisible(each.english, e.target.checked)}
                                                    />
                                                </div>
                                            </div>
                                            {
                                                isDeadlineEditing && editDeadlines &&
                                                <div style={{ background: "#fafafa", borderRadius: "6px", padding: "12px", margin: "0 4px 10px 4px", borderBottom: "1px solid #f0f0f0" }}>
                                                    {
                                                        deadlineEditRows.map((row) => {
                                                            return (
                                                                <div key={row.key} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                                                                    <div style={{ width: "90px", fontSize: "13px" }}>{row.label}</div>
                                                                    <select
                                                                        value={editDeadlines[row.key].daysBefore}
                                                                        onChange={(e) => {
                                                                            const value = +e.target.value;
                                                                            setEditDeadlines((prev: any) => ({ ...prev, [row.key]: { ...prev[row.key], daysBefore: value } }));
                                                                        }}
                                                                        style={{ height: "40px", marginRight: "8px", padding: "0 4px", borderRadius: "4px", border: "1px solid #c4c4c4", background: "white" }}
                                                                    >
                                                                        {
                                                                            [0, 1, 2, 3, 4, 5, 6, 7].map((day) => {
                                                                                return (
                                                                                    <option key={day} value={day}>{day === 0 ? "당일" : `${day}일 전`}</option>
                                                                                );
                                                                            })
                                                                        }
                                                                    </select>
                                                                    <OutlinedInput
                                                                        size="small"
                                                                        type="time"
                                                                        sx={{ width: "170px" }}
                                                                        value={editDeadlines[row.key].time}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            setEditDeadlines((prev: any) => ({ ...prev, [row.key]: { ...prev[row.key], time: value } }));
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "4px" }}>
                                                        <Button
                                                            size="sm"
                                                            variant="solid"
                                                            loading={savingDeadlines}
                                                            onClick={() => submitDeadlines(each.english)}
                                                        >
                                                            저장
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="plain"
                                                            color="neutral"
                                                            sx={{ marginLeft: "4px" }}
                                                            onClick={() => { setEditingDeadlineLocation(""); setEditDeadlines(null); }}
                                                        >
                                                            취소
                                                        </Button>
                                                    </div>
                                                </div>
                                            }
                                            </React.Fragment>
                                        );

                                    })
                                }
                            </div>
                    }
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default RegularMealSettingModal;
